import React from 'react';
import { X, Keyboard } from 'lucide-react';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcuts = [
  { category: 'Navigation', items: [
    { keys: ['1', '2', '3', '4', '5', '6'], description: 'Jump to view (Overview, Macro, FDI, Sectors, Peers, Events)' },
    { keys: ['←', '→'], description: 'Previous / Next view' },
  ]},
  { category: 'Search', items: [
    { keys: ['⌘', 'K'], description: 'Open search (Ctrl+K on Windows)' },
    { keys: ['↑', '↓'], description: 'Navigate search results' },
    { keys: ['↵'], description: 'Select search result' },
  ]},
  { category: 'General', items: [
    { keys: ['?'], description: 'Show/hide keyboard shortcuts' },
    { keys: ['Esc'], description: 'Close dialogs and menus' },
  ]},
];

export const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 px-4">
        <div className="bg-terminal-panel border border-terminal-border rounded-lg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-terminal-border bg-terminal-header">
            <div className="flex items-center gap-2">
              <Keyboard size={16} className="text-data-amber" />
              <span className="text-sm font-semibold text-white">Keyboard Shortcuts</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-white transition-colors rounded hover:bg-terminal-border"
            >
              <X size={16} />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {shortcuts.map(section => (
              <div key={section.category} className="mb-4 last:mb-0">
                <h3 className="text-[10px] font-semibold text-data-amber uppercase tracking-wider mb-2">
                  {section.category}
                </h3>
                <div className="space-y-2">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-4">
                      <span className="text-xs text-gray-400">{item.description}</span>
                      <div className="flex items-center gap-1">
                        {item.keys.map((key, keyIdx) => (
                          <React.Fragment key={keyIdx}>
                            <kbd className="px-2 py-0.5 bg-terminal-header border border-terminal-border rounded text-[10px] text-gray-300 font-mono min-w-[24px] text-center">
                              {key}
                            </kbd>
                            {keyIdx < item.keys.length - 1 && item.keys.length === 2 && key === '⌘' && (
                              <span className="text-gray-600 text-[10px]">+</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-terminal-border bg-terminal-header">
            <p className="text-[9px] text-gray-500 text-center">
              Press <kbd className="px-1 py-0.5 bg-terminal-border rounded text-[9px]">?</kbd> again to close
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default KeyboardShortcutsHelp;
